import type { PlanUsage } from './platformApi';

/** Matches API `assert_seat_available` / monthly / storage dimension keys. */
export type PlanLimitDimension =
  | 'max_students'
  | 'max_teachers'
  | 'max_counselors'
  | 'max_parents'
  | 'max_classes'
  | 'monthly_email'
  | 'monthly_sms'
  | 'monthly_push'
  | 'max_storage_mb';

export const DIMENSION_LABELS: Record<PlanLimitDimension, string> = {
  max_students: 'Students',
  max_teachers: 'Teachers',
  max_counselors: 'Counselors',
  max_parents: 'Parents',
  max_classes: 'Classes',
  monthly_email: 'Email sends (this month)',
  monthly_sms: 'SMS sends (this month)',
  monthly_push: 'Push sends (this month)',
  max_storage_mb: 'Storage',
};

const USAGE_BY_LIMIT: Record<PlanLimitDimension, keyof PlanUsage['usage']> = {
  max_students: 'students',
  max_teachers: 'teachers',
  max_counselors: 'counselors',
  max_parents: 'parents',
  max_classes: 'classes',
  monthly_email: 'email_month',
  monthly_sms: 'sms_month',
  monthly_push: 'push_month',
  max_storage_mb: 'storage_mb',
};

const ROLE_SEAT_LIMIT: Record<string, PlanLimitDimension> = {
  teacher: 'max_teachers',
  counselor: 'max_counselors',
  parent: 'max_parents',
};

const CHANNEL_MONTHLY: Record<string, PlanLimitDimension> = {
  email: 'monthly_email',
  sms: 'monthly_sms',
  push: 'monthly_push',
};

export type PlanLimitBlockPayload =
  | {
      kind: 'quota';
      dimension: PlanLimitDimension;
      cap: number;
      used: number;
      plan?: string;
      additional?: number;
    }
  | {
      kind: 'channel';
      channel: string;
      plan?: string;
    };

type PlanLimitOpener = (payload: PlanLimitBlockPayload) => void;

let planLimitOpener: PlanLimitOpener | null = null;

/** Registered by PlanLimitProvider — opens the upgrade modal. */
export function registerPlanLimitOpener(opener: PlanLimitOpener | null): () => void {
  planLimitOpener = opener;
  return () => {
    if (planLimitOpener === opener) planLimitOpener = null;
  };
}

export function openPlanLimitBlock(payload: PlanLimitBlockPayload): void {
  if (planLimitOpener) {
    planLimitOpener(payload);
    return;
  }
  // Provider not mounted yet — last resort
  // eslint-disable-next-line no-console
  console.warn('[planLimits]', planLimitReachedMessageFromPayload(payload));
}

function planLimitReachedMessageFromPayload(payload: PlanLimitBlockPayload): string {
  if (payload.kind === 'channel') {
    return `Plan does not allow channel '${payload.channel}'. Upgrade plan or change channel.`;
  }
  return planLimitReachedMessage(payload.dimension, payload.cap);
}

/** Same wording as API ForbiddenError so operators recognize it. */
export function planLimitReachedMessage(dimension: PlanLimitDimension, cap: number): string {
  if (dimension.startsWith('monthly_')) {
    return `Plan limit reached: ${dimension} (${cap}/mo). Upgrade plan or wait until next month.`;
  }
  if (dimension === 'max_storage_mb') {
    return `Plan limit reached: max_storage_mb (${cap}). Upgrade plan or free storage.`;
  }
  return `Plan limit reached: ${dimension} (${cap}). Upgrade plan or free seats.`;
}

export function planLimitCap(
  usage: PlanUsage | null | undefined,
  dimension: PlanLimitDimension,
): number | null {
  if (!usage) return null;
  const cap = usage.limits[dimension];
  return cap == null ? null : Number(cap);
}

export function planLimitUsed(
  usage: PlanUsage | null | undefined,
  dimension: PlanLimitDimension,
): number {
  if (!usage) return 0;
  return Number(usage.usage[USAGE_BY_LIMIT[dimension]] ?? 0);
}

/** True when a finite cap is set and current usage is already at/over it. */
export function isPlanLimitReached(
  usage: PlanUsage | null | undefined,
  dimension: PlanLimitDimension,
  additional = 1,
): boolean {
  const cap = planLimitCap(usage, dimension);
  if (cap == null) return false;
  return planLimitUsed(usage, dimension) + additional > cap;
}

/**
 * If the school is at this plan quota, open the upgrade modal and return false.
 * Call on create-button click (before opening a form / calling the API).
 */
export function guardPlanLimit(
  usage: PlanUsage | null | undefined,
  dimension: PlanLimitDimension,
  additional = 1,
): boolean {
  const cap = planLimitCap(usage, dimension);
  if (cap == null) return true;
  const used = planLimitUsed(usage, dimension);
  if (used + additional > cap) {
    openPlanLimitBlock({
      kind: 'quota',
      dimension,
      cap,
      used,
      plan: usage?.plan,
      additional,
    });
    return false;
  }
  return true;
}

export function seatDimensionForRole(role: string): PlanLimitDimension | null {
  return ROLE_SEAT_LIMIT[role] ?? null;
}

export function guardPlanRoleSeat(
  usage: PlanUsage | null | undefined,
  role: string,
): boolean {
  const dim = seatDimensionForRole(role);
  if (!dim) return true;
  return guardPlanLimit(usage, dim);
}

export function guardPlanChannel(
  usage: PlanUsage | null | undefined,
  channel: string,
): boolean {
  const channels = usage?.limits.channels;
  if (channels && channel in channels && !channels[channel as 'email' | 'sms' | 'push']) {
    openPlanLimitBlock({
      kind: 'channel',
      channel,
      plan: usage?.plan,
    });
    return false;
  }
  const dim = CHANNEL_MONTHLY[channel];
  if (!dim) return true;
  return guardPlanLimit(usage, dim);
}

export function guardPlanStorageMb(
  usage: PlanUsage | null | undefined,
  additionalBytes: number,
): boolean {
  const addMb = Math.max(1, Math.ceil(additionalBytes / (1024 * 1024)));
  return guardPlanLimit(usage, 'max_storage_mb', addMb);
}

/** Parse API ForbiddenError detail into a modal payload. */
export function parsePlanLimitApiMessage(
  message: string,
): PlanLimitBlockPayload | null {
  const channelMatch = message.match(/Plan does not allow channel '([^']+)'/i);
  if (channelMatch) {
    return { kind: 'channel', channel: channelMatch[1] };
  }
  const quotaMatch = message.match(
    /Plan limit reached:\s*(max_students|max_teachers|max_counselors|max_parents|max_classes|monthly_email|monthly_sms|monthly_push|max_storage_mb)\s*\((\d+)/i,
  );
  if (quotaMatch) {
    const dimension = quotaMatch[1] as PlanLimitDimension;
    const cap = Number(quotaMatch[2]);
    return {
      kind: 'quota',
      dimension,
      cap,
      used: cap,
    };
  }
  return null;
}

export function isPlanLimitApiMessage(message: string): boolean {
  return (
    /Plan limit reached:/i.test(message) || /Plan does not allow channel/i.test(message)
  );
}
